// Taking a photograph off the preview: sampling frames, scoring, encoding and thumbnails.
import { createAnalyzer } from "../../lib/captureQuality";


/**
 * The longest edge we keep.
 *
 * Raised from 2048. The old value came with the reasoning that "the pack shot
 * pipeline normalises to 2048 and the extraction call reads fine print, so past
 * this we are paying for pixels nobody reads" — but the fine print is exactly
 * the case it was wrong about. A nutrition panel photographed as part of a
 * whole pack occupies a fraction of the frame, and at a 2048 long edge that
 * fraction is a few hundred pixels of 6pt text. The panel is what extraction
 * most needs and what a merchant least wants to re-shoot.
 *
 * ⚠️ THIS COSTS MONEY PER ENRICHMENT RUN, NOT JUST STORAGE. packshot.service's
 * `imageToContent` sends stored images to the model at whatever resolution they
 * were saved at — it EXIF-rotates and base64s, and never resizes. So every
 * pixel kept here is paid for again on every run that reads this photograph.
 * 3072 is 2.25x the pixels of 2048, and full sensor would be nearer 3x. If the
 * per-run cost matters more than the fine print, the right fix is a resize in
 * `imageToContent` rather than throwing the pixels away here, where they cannot
 * be recovered.
 */
const SHOT_MAX_EDGE = 3072;

/**
 * 0.9, up from 0.85. Small text is where JPEG artefacts do the most damage, and
 * the ringing around high-contrast glyphs is exactly what makes an ingredients
 * panel unreadable to a model. Costs roughly 30% in bytes for the thing the
 * photograph is being taken for.
 */
const SHOT_QUALITY = 0.9;

/**
 * Release both object URLs a shot can hold.
 *
 * A shot carries two for most of its life: the instant preview and, once the
 * real capture lands, the full-resolution image. Freeing only one leaks the
 * other for the life of the page, and a shelf walk makes hundreds of them.
 */
export function releaseShot(shot) {
  if (shot?.previewUrl) URL.revokeObjectURL(shot.previewUrl);
  if (shot?.url) URL.revokeObjectURL(shot.url);
}

/**
 * Variance of the Laplacian on a small greyscale copy — "how sharp is this".
 *
 * Deliberately scored on a fixed ~256px draw: the metric is scale dependent, so
 * comparing frames of different sizes says nothing, and reading back a
 * full-resolution frame every sample would cost more than the capture.
 */
const SCORE_EDGE = 256;

const analyzer = createAnalyzer();

/** One 256px draw of the frame: sharpness and the rest of the quality metrics. */
export function analyzeCanvas(source, sw, sh, scratch) {
  const scale = Math.min(1, SCORE_EDGE / Math.max(sw, sh));
  const w = Math.max(8, Math.round(sw * scale));
  const h = Math.max(8, Math.round(sh * scale));
  if (scratch.width !== w || scratch.height !== h) {
    scratch.width = w;
    scratch.height = h;
  }
  const ctx = scratch.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, w, h);
  return analyzer.analyze(ctx.getImageData(0, 0, w, h).data, w, h);
}

export const DEBUG_SCORE =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("debug") === "1";

/** Merchants see only the flags that are proven; the rest are recorded and shown with ?debug=1. */
export const shownFlag = (f) => (f && (DEBUG_SCORE || f === "blurry" || f === "dark") ? f : null);

/** How many preview frames to consider, and over how long. */
const FRAME_SAMPLES = 5;

const FRAME_GAP_MS = 55;

/**
 * The sharpest of several consecutive preview frames, at up to SHOT_MAX_EDGE.
 *
 * ---------------------------------------------------------------------
 * WHY NOT ImageCapture.takePhoto()
 * ---------------------------------------------------------------------
 * Because it was measured, on real captures from this project, to be the
 * BLURRY path. Uploads split cleanly into two populations by aspect ratio:
 * 3:4 frames (the sensor's stills aspect, i.e. takePhoto) scored 6-16 on
 * variance-of-Laplacian at 121-165KB, while 9:16 frames (the preview, i.e.
 * this function's ancestor) scored 700-1850 at 466-675KB. Same device, same
 * session, same subject distance.
 *
 * The reason is autofocus. takePhoto() pulls from the stills pipeline as soon
 * as it is asked, frequently before AF and AE converge, and there is no
 * reliable web API to make it wait. The preview stream, by contrast, has
 * continuous autofocus running the whole time — so a frame taken off it is
 * focused almost by definition.
 *
 * This also unifies the platforms: iOS Safari has no ImageCapture at all, so
 * the preview path was already the only path there.
 *
 * Sampling several frames and keeping the best one then removes most of what
 * is left, which is hand shake.
 */
/*
 * HOW IT HOLDS MEMORY AND STAYS TRUE TO THE FRAME
 *
 * Sampling starts AT THE TAP and is never queued behind another capture, so a
 * quick front, back, panel sequence samples each shot when it was pressed.
 * Each sample is ONE createImageBitmap(video): the bitmap is scored (drawn to
 * a ~256px canvas) and is also what gets saved, so the scored frame and the
 * saved frame are the same pixels. Only the current best bitmap is kept and
 * every other one is closed at once, so a capture holds at most two full-size
 * bitmaps. The saved size comes from the bitmap's own width and height, which
 * also makes a mid-sample track resize harmless.
 *
 * Only the full-size draw and the encode are serialised (one shared work
 * canvas, freed after each encode; iOS caps total canvas memory).
 */
export async function captureBestFrame(video, isWanted = () => true) {
  let best = null;
  try {
    for (let i = 0; i < FRAME_SAMPLES; i++) {
      // A stream that drops out mid-sample: skip this frame.
      if (video.videoWidth && video.videoHeight) {
        const frame = await grabFrame(video);
        if (frame) {
          let metrics = null;
          try {
            metrics = analyzeCanvas(frame.src, frame.width, frame.height, scratchCanvas);
          } catch {
            /* unscorable frame: skipped below */
          }
          if (!metrics) {
            frame.close();
          } else if (!best || metrics.sharp > best.metrics.sharp) {
            best?.frame.close();
            best = { frame, metrics };
          } else {
            frame.close();
          }
        }
      }
      if (i < FRAME_SAMPLES - 1) {
        await new Promise((r) => setTimeout(r, FRAME_GAP_MS));
      }
    }
    // A zero score is a blank or black frame, not a photograph.
    if (!best || best.metrics.sharp === 0) return null;
    // Discarded or unmounted while sampling or queued: skip the encode.
    if (!isWanted()) return null;
    const out = await encodeFrame(best.frame, isWanted);
    return out ? { ...out, score: best.metrics.sharp, metrics: best.metrics } : null;
  } finally {
    best?.frame.close();
  }
}

/** One frame off the live preview, as something drawImage accepts. */
async function grabFrame(video) {
  // Sized at the source so no full 4K bitmap is held. resizeWidth only: the
  // browser derives the height from the real frame, so a rotation race cannot
  // squeeze it. Both dimensions are read in this tick. Default resize quality
  // matches the old downscale(); "high" cost ~300 ms more main thread per press.
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const resizeWidth = Math.round(vw * Math.min(1, SHOT_MAX_EDGE / Math.max(vw, vh)));
  try {
    const bmp = await createImageBitmap(video, { resizeWidth });
    return { src: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  } catch {
    // No createImageBitmap(video) on this browser: snapshot to a canvas.
    const c = document.createElement("canvas");
    c.width = vw;
    c.height = vh;
    const cctx = c.getContext("2d");
    if (!cctx) {
      freeCanvas(c);
      return null;
    }
    cctx.drawImage(video, 0, 0);
    return { src: c, width: vw, height: vh, close: () => freeCanvas(c) };
  }
}

export const scratchCanvas = document.createElement("canvas");

const workCanvas = document.createElement("canvas");

const thumbCanvas = document.createElement("canvas");

let encodeChain = Promise.resolve();

/** Release a canvas's backing store now rather than when the GC gets to it. */
function freeCanvas(c) {
  c.width = 0;
  c.height = 0;
}

export function encodeFrame(frame, isWanted) {
  const run = encodeChain.then(() => (isWanted() ? encodeFrameNow(frame) : null));
  encodeChain = run.catch(() => {});
  return run;
}

/** The one JPEG encode of the shot, plus a thumbnail of the same frame. */
async function encodeFrameNow({ src, width, height }) {
  const work = workCanvas;
  const ctx = work.getContext("2d");
  if (!ctx) return null;
  try {
    // Native size, capped at SHOT_MAX_EDGE on the long edge.
    const scale = Math.min(1, SHOT_MAX_EDGE / Math.max(width, height));
    work.width = Math.round(width * scale);
    work.height = Math.round(height * scale);
    ctx.drawImage(src, 0, 0, work.width, work.height);
    const blob = await new Promise((resolve) =>
      work.toBlob(resolve, "image/jpeg", SHOT_QUALITY),
    );
    if (!blob) return null;

    // The tile shows the frame that was filed, not the tap-time placeholder.
    let thumb = null;
    try {
      const t = Math.min(1, 320 / Math.max(width, height));
      thumbCanvas.width = Math.max(1, Math.round(width * t));
      thumbCanvas.height = Math.max(1, Math.round(height * t));
      thumbCanvas.getContext("2d")?.drawImage(work, 0, 0, thumbCanvas.width, thumbCanvas.height);
      thumb = await new Promise((resolve) => thumbCanvas.toBlob(resolve, "image/jpeg", 0.6));
    } catch {
      /* the placeholder stays */
    }
    return { blob, thumb };
  } finally {
    freeCanvas(work);
    freeCanvas(thumbCanvas);
  }
}

/**
 * A thumbnail-sized frame off the running preview.
 *
 * This is what makes the shutter feel instant, so it has to be cheap: one
 * drawImage at ~320px and a low-quality encode, which is a millisecond or two.
 * Drawing the full sensor frame to produce a placeholder would cost a
 * meaningful share of what the real capture costs and defeat the point.
 *
 * It is a PLACEHOLDER, never the image we upload — the tile it fills is marked
 * pending until the real capture replaces it.
 */
export async function previewFrame(video) {
  try {
    // Read here, not from a value takeShot measured earlier, for the same
    // reason captureBestFrame does: this thumbnail's ASPECT is what sizes the
    // tile, and a tile the wrong shape for its own photograph would letterbox
    // for a beat and then jump when the real capture lands.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return "";
    const scale = Math.min(1, 320 / Math.max(vw, vh));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(vw * scale));
    canvas.height = Math.max(1, Math.round(vh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.6),
    );
    freeCanvas(canvas);
    return blob ? URL.createObjectURL(blob) : "";
  } catch {
    // A tile with no thumbnail is still a tile: the count is right and the
    // capture continues. Losing the placeholder is cosmetic.
    return "";
  }
}

/** A small JPEG of a shot, kept as a data URL: the shot's own object URLs are revoked on hand-off. */
export async function thumbOf(blob, edge = 160) {
  const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
  const k = Math.min(1, edge / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(bmp.width * k));
  c.height = Math.max(1, Math.round(bmp.height * k));
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  return c.toDataURL("image/jpeg", 0.7);
}